import './styles.scoped.css'
import {useState, useEffect} from 'react'
import {Typography, TextField, Button, IconButton, Accordion, AccordionSummary, AccordionDetails} from '@mui/material'
import {Add, Edit, ExpandMore, Delete, Close, Save} from '@mui/icons-material'
import {useWatch} from 'react-hook-form'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {clientApi} from '../../../../services'
import {parseError} from '../../../../utils/helpers'
import {format} from 'date-fns'
import {SkeletonContainer, TextSkeleton} from '../../../../components'

export default ({form, assignedServices}) => {
    const {setMessageBox, branchId} = useGlobalStates()
    const [serviceItems, setServiceItems] = useState([])
    const [skeleton, setSkeleton] = useState(true)
    const [rows, setRows] = useState([])
    const [newRows, setNewRows] = useState([])
    const [editingRows, setEditingRows] = useState({})
    const formValues = useWatch({control: form.control})

    useEffect(() => {
        setSkeleton(true)
        clientApi
            .getBranchServices({branch_id: branchId})
            .then(({data: {data}}) => {
                setSkeleton(false)
                setServiceItems(data)
            })
            .catch(error => {
                setSkeleton(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }, [branchId])

    useEffect(() => {
        if (formValues?.activities) {
            const newVal = formValues?.activities?.map((item, index) => ({
                ...item,
                row_id: index,
            }))
            if (JSON.stringify(newVal) != JSON.stringify(rows)) {
                setRows(newVal)
            }
        }
    }, [formValues])

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (rows) {
                const newVal = rows?.map(item => item)
                if (JSON.stringify(newVal) != JSON.stringify(form.getValues('activities'))) {
                    form.setValue('activities', newVal)
                }
            }
        }, 100)
        return () => clearTimeout(timeout)
    }, [rows])

    useEffect(() => {
        for (const item of assignedServices ?? []) {
            if (!newRows?.find(r => r.service_unique_id === item?.unique_id)) {
                addNewRow(item?.id, item?.unique_id)
            }
        }
    }, [newRows])

    const addNewRow = (service_item_id, service_unique_id) => {
        const new_row_id = Date.now()
        setNewRows([...newRows, {new_row_id, id: '', service_item_id, source_type: 'custom', activity_name: '', instructions: '', service_unique_id}])
    }

    const newRowChange = (new_row_id, field, value) => {
        setNewRows(prev => prev.map(r => (r.new_row_id === new_row_id ? {...r, [field]: value} : r)))
    }

    const saveNewRow = (new_row_id, assignedService) => {
        const rowToSave = newRows.find(r => r.new_row_id === new_row_id)
        if (!rowToSave) return

        if (!assignedService?.id) {
            rowToSave.service_unique_id = `${assignedService?.branch_service_id}|${format(assignedService?.start_date, 'yyyyMMdd')}|${format(assignedService?.end_date, 'yyyyMMdd')}`
        }

        const row_id = rows.length ? Math.max(...rows.map(r => r.row_id)) + 1 : 1
        setRows([...rows, {...rowToSave, row_id}])
        setNewRows(newRows.filter(r => r.new_row_id !== new_row_id))
    }

    const deleteRow = row_id => {
        setRows(rows.filter(r => r.row_id !== row_id))
        const updated = {...editingRows}
        delete updated[row_id]
        setEditingRows(updated)
    }

    const editRow = row => {
        setEditingRows(prev => ({
            ...prev,
            [row.row_id]: prev[row.row_id] ? undefined : {...row},
        }))
    }

    const editRowChange = (row_id, field, value) => {
        setEditingRows(prev => ({
            ...prev,
            [row_id]: {
                ...prev[row_id],
                [field]: value,
            },
        }))
    }

    const saveEditRow = (row_id, assignedService) => {
        const rowToSave = editingRows[row_id]

        if (!assignedService?.id) {
            rowToSave.service_unique_id = `${assignedService?.branch_service_id}|${format(assignedService?.start_date, 'yyyyMMdd')}|${format(assignedService?.end_date, 'yyyyMMdd')}`
        }

        setRows(prev => prev.map(r => (r.row_id === row_id ? rowToSave : r)))
        const updated = {...editingRows}
        delete updated[row_id]
        setEditingRows(updated)
    }

    const cancelEditRow = row_id => {
        const updated = {...editingRows}
        delete updated[row_id]
        setEditingRows(updated)
    }

    return (
        <>
            {assignedServices?.map((assignedService, index) => (
                <Accordion key={index}>
                    <AccordionSummary expandIcon={<ExpandMore sx={{fontSize: 32}} />}>
                        <div className="title">
                            <Typography variant="h9" color="primary">
                                {String(index + 1).padStart(2, '0')}
                            </Typography>
                            {skeleton ? (
                                <SkeletonContainer>
                                    <TextSkeleton width={150} fontSize={18} />
                                    <TextSkeleton width={110} sx={{mt: 0.5}} />
                                </SkeletonContainer>
                            ) : (
                                <div>
                                    <Typography variant="h7" color="primary.dark">
                                        {serviceItems?.find(item => item?.id == assignedService?.branch_service_id)?.service_name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" style={{marginTop: 4}}>
                                        {serviceItems?.find(item => item?.id == assignedService?.branch_service_id)?.hcpc_code}-
                                        {serviceItems?.find(item => item?.id == assignedService?.branch_service_id)?.billing_unit?.replace('_', ' ')}
                                    </Typography>
                                </div>
                            )}
                        </div>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="inputLabel" color="text.secondary">
                            Activity
                        </Typography>
                        <br />
                        <div className="activity-container">
                            {rows.map(row => {
                                if (row?.service_unique_id == assignedService?.unique_id) {
                                    const isEditing = !!editingRows[row.row_id]
                                    const editValues = editingRows[row.row_id] || {}
                                    return (
                                        <div key={row.row_id}>
                                            {isEditing ? (
                                                <>
                                                    <br />
                                                    <div className="activity-card">
                                                        <div className="activity-card-actions">
                                                            <div className="activity-card-actions-left">
                                                                <TextField
                                                                    fullWidth
                                                                    placeholder="Enter activity name"
                                                                    value={editValues.activity_name}
                                                                    onChange={e => editRowChange(row.row_id, 'activity_name', e.target.value)}
                                                                />
                                                                <br />
                                                                <TextField
                                                                    placeholder="Enter instructions details or notes..."
                                                                    multiline
                                                                    rows={4}
                                                                    fullWidth
                                                                    style={{marginTop: 8}}
                                                                    value={editValues.instructions}
                                                                    onChange={e => editRowChange(row.row_id, 'instructions', e.target.value)}
                                                                />
                                                            </div>
                                                            <div className="activity-card-actions-right">
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    onClick={() => saveEditRow(row.row_id, assignedService)}>
                                                                    <Save fontSize="small" />
                                                                </IconButton>
                                                                <IconButton size="small" color="error" onClick={() => cancelEditRow(row.row_id)}>
                                                                    <Close fontSize="small" />
                                                                </IconButton>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <br />
                                                    <div className="activity-card">
                                                        <div className="activity-card-top">
                                                            <Typography variant="body1" color="text.primary">
                                                                {row?.activity_name}
                                                            </Typography>
                                                            <div>
                                                                <IconButton size="small" color="primary" onClick={() => editRow(row)}>
                                                                    <Edit fontSize="small" />
                                                                </IconButton>
                                                                <IconButton size="small" color="error" onClick={() => deleteRow(row?.row_id)}>
                                                                    <Delete fontSize="small" />
                                                                </IconButton>
                                                            </div>
                                                        </div>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {row?.instructions}
                                                        </Typography>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )
                                }
                            })}
                            {newRows.map((row, index) => {
                                if (row?.service_unique_id == assignedService?.unique_id) {
                                    return (
                                        <div key={index}>
                                            <br />
                                            <div className="activity-card">
                                                <div className="activity-card-actions">
                                                    <div className="activity-card-actions-left">
                                                        <TextField
                                                            fullWidth
                                                            placeholder="Enter activity name"
                                                            value={row.activity_name}
                                                            onChange={e => newRowChange(row.new_row_id, 'activity_name', e.target.value)}
                                                        />
                                                        <br />
                                                        <TextField
                                                            placeholder="Enter instructions details or notes..."
                                                            multiline
                                                            rows={4}
                                                            fullWidth
                                                            style={{marginTop: 8}}
                                                            value={row.instructions}
                                                            onChange={e => newRowChange(row.new_row_id, 'instructions', e.target.value)}
                                                        />
                                                    </div>
                                                    <Button
                                                        variant="text"
                                                        startIcon={<Add />}
                                                        color="primary"
                                                        onClick={() => saveNewRow(row.new_row_id, assignedService)}>
                                                        Add
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                }
                            })}
                        </div>
                    </AccordionDetails>
                </Accordion>
            ))}
        </>
    )
}
